import React, { useMemo, useState, useEffect } from "react";
import { useCommunities, useGlobalProperties } from "../hooks/useSuperAdmin";
import { Icon } from "@iconify/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useMe } from "../hooks/useMe";
import Logo from "../components/assets/logo.png";
import { Loader } from "lucide-react";
import { Table } from "antd";
import CompetingAgentManagement from "./competingAgentManagement";
import ImportProperties from "../components/ImportProperties";
import Team from "./teammanager";

const PROJECT_ADMIN_COMMUNITY_KEY = "projectAdminCommunityId";

const normalizeHomeTypeForCount = (ht) => {
  if (!ht) return null;
  const t = String(ht).toUpperCase().replace(/\s/g, "");
  if (t === "SFH") return "SFH";
  if (t === "CONDO") return "CONDO";
  if (t === "TH") return "TH";
  if (t === "VILLA" || t === "VIL") return "VIL";
  return null;
};

const menuItemsWhenSelected = [
  { id: "competition", name: "Competition", icon: "mdi:account-tie" },
  { id: "upload", name: "Upload", icon: "mdi:file-upload" },
  { id: "schools", name: "Schools", icon: "mdi:school" },
  { id: "team", name: "Team", icon: "mdi:account-group" },
];

const ProjectAdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { communities, isLoading: communitiesLoading } = useCommunities();
  const { properties, isLoading: propertiesLoading } = useGlobalProperties();

  const [activeTab, setActiveTab] = useState("competition");
  const [selectedCommunityIdState, setSelectedCommunityIdState] =
    useState(null);
  const selectedCommunityId = selectedCommunityIdState;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const setSelectedCommunityId = (id) => {
    const numId = id != null ? Number(id) : null;
    setSelectedCommunityIdState(numId);
    if (numId != null) {
      setSearchParams({ community: numId });
      sessionStorage.setItem(PROJECT_ADMIN_COMMUNITY_KEY, String(numId));
      localStorage.setItem("communityId", String(numId));
    } else {
      setSearchParams({});
      sessionStorage.removeItem(PROJECT_ADMIN_COMMUNITY_KEY);
      localStorage.removeItem("communityId");
    }
  };

  useEffect(() => {
    const fromUrl = searchParams.get("community");
    const fromStorage = sessionStorage.getItem(PROJECT_ADMIN_COMMUNITY_KEY);
    const raw = fromUrl || fromStorage;
    if (raw) {
      const id = parseInt(raw, 10);
      if (Number.isFinite(id)) setSelectedCommunityIdState(id);
      if (id && !fromUrl) setSearchParams({ community: id });
      if (id) localStorage.setItem("communityId", String(id));
    }
  }, []);

  const { logout } = useAuth();
  const navigate = useNavigate();
  const { me } = useMe();

  const handleLogOut = async () => {
    logout();
    navigate("/");
  };

  const goToAgentPanel = (communityId) => {
    localStorage.setItem("communityId", String(communityId));
    navigate("/agent-admin");
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const communitiesTableData = useMemo(() => {
    const countsByCommunity = {};
    (properties || []).forEach((p) => {
      const cid = p.community_id ?? p.community?.id;
      if (cid == null) return;
      if (!countsByCommunity[cid]) {
        countsByCommunity[cid] = { SFH: 0, CONDO: 0, TH: 0, VIL: 0 };
      }
      const key = normalizeHomeTypeForCount(p.home_type);
      if (key && countsByCommunity[cid].hasOwnProperty(key)) {
        countsByCommunity[cid][key]++;
      }
    });
    return (communities || []).map((c) => ({
      ...c,
      shortName: c.shortName ?? c.short_name ?? c.name,
      SFH: countsByCommunity[c.id]?.SFH ?? 0,
      CONDO: countsByCommunity[c.id]?.CONDO ?? 0,
      TH: countsByCommunity[c.id]?.TH ?? 0,
      VIL: countsByCommunity[c.id]?.VIL ?? 0,
    }));
  }, [communities, properties]);

  const communitiesData = useMemo(() => communities || [], [communities]);

  if (communitiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen font-Poppins lg:flex-row">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full bg-red-600 text-white shadow-md w-52
          transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          transition-transform duration-300 z-30
          lg:static lg:translate-x-0 lg:w-52
          flex flex-col
        `}
      >
        <div className="flex items-center justify-between py-4 px-3 flex-shrink-0">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="w-10 h-10 object-contain" />
            <span className="text-sm font-semibold hidden lg:inline-block ml-2">
              Project Admin Panel
            </span>
          </div>
          <button
            type="button"
            className="lg:hidden text-white focus:outline-none"
            onClick={toggleSidebar}
            aria-label="Close Sidebar"
          >
            <Icon icon="mdi:close" className="text-xl" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-2">
          <button
            type="button"
            onClick={() => {
              setSelectedCommunityId(null);
              setActiveTab("competition");
              setIsSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
              !selectedCommunityId
                ? "bg-red-500 text-white"
                : "hover:bg-red-700 hover:text-white"
            }`}
          >
            <Icon icon="mdi:home" className="text-lg" />
            <span className="ml-1">Home</span>
          </button>
          {!selectedCommunityId && (
            <p className="px-3 py-2 text-xs text-red-100 italic">
              No other tabs appear here until a community is selected in the
              table.
            </p>
          )}
          {selectedCommunityId != null &&
            menuItemsWhenSelected.map(({ id, name, icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setActiveTab(id);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
                  activeTab === id
                    ? "bg-red-500 text-white"
                    : "hover:bg-red-700 hover:text-white"
                }`}
                aria-current={activeTab === id ? "page" : undefined}
              >
                <Icon icon={icon} className="text-lg" />
                <span className="ml-1">{name}</span>
              </button>
            ))}
        </nav>

        <div className="flex-shrink-0 px-2 pb-3">
          <button
            type="button"
            onClick={handleLogOut}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md font-medium text-white hover:bg-red-700 transition"
          >
            <Icon icon="mdi:logout" className="text-lg" />
            <span className="ml-1">Logout</span>
          </button>
        </div>
      </aside>

      <header className="flex items-center justify-between bg-red-600 text-white p-4 lg:hidden">
        <button
          type="button"
          onClick={toggleSidebar}
          className="focus:outline-none"
          aria-label="Open Sidebar"
        >
          <Icon icon="mdi:menu" className="text-2xl" />
        </button>
        <img src={Logo} className="w-8 h-8 object-contain" alt="Logo" />
        <span className="text-lg font-semibold">Project Admin Panel</span>
        <div className="w-8 h-8" />
      </header>

      <main className="flex-1 bg-gray-100 h-screen overflow-y-auto">
        <div className="max-w-7xl mx-auto p-4 lg:p-8">
          <div className="mb-4">
            <p className="text-lg font-semibold text-gray-900">
              {me?.name ?? "Project Admin"}
            </p>
            {selectedCommunityId != null && (
              <p className="text-xl font-bold text-red-700 uppercase mt-1">
                {communitiesData.find((c) => c.id === selectedCommunityId)
                  ?.shortName ??
                  communitiesData.find((c) => c.id === selectedCommunityId)
                    ?.name ??
                  "Community"}
              </p>
            )}
          </div>

          {!selectedCommunityId ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-4 uppercase">
                Home Page
              </h1>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <Table
                  dataSource={communitiesTableData}
                  rowKey="id"
                  pagination={false}
                  loading={propertiesLoading}
                  columns={[
                    {
                      title: "Name",
                      dataIndex: "shortName",
                      key: "shortName",
                      render: (text, record) => (
                        <button
                          type="button"
                          onClick={() => setSelectedCommunityId(record.id)}
                          className="text-red-600 font-semibold hover:underline uppercase text-left"
                        >
                          {text || record.name}
                        </button>
                      ),
                    },
                    {
                      title: "Panel",
                      key: "panel",
                      render: (_, record) => (
                        <button
                          type="button"
                          onClick={() => goToAgentPanel(record.id)}
                          className="text-blue-600 hover:underline"
                        >
                          AGENT
                        </button>
                      ),
                    },
                    {
                      title: "SFH",
                      dataIndex: "SFH",
                      key: "SFH",
                      width: 72,
                    },
                    {
                      title: "Condo",
                      dataIndex: "CONDO",
                      key: "CONDO",
                      width: 80,
                    },
                    { title: "TH", dataIndex: "TH", key: "TH", width: 72 },
                    { title: "Villa", dataIndex: "VIL", key: "VIL", width: 72 },
                  ]}
                />
              </div>
            </>
          ) : (
            <>
              {activeTab === "competition" && (
                <div className="bg-white rounded-lg shadow p-6">
                  <CompetingAgentManagement />
                </div>
              )}
              {activeTab === "upload" && (
                <div className="bg-white rounded-lg shadow p-6">
                  <ImportProperties communityId={String(selectedCommunityId)} />
                </div>
              )}
              {activeTab === "schools" && (
                <div className="bg-white rounded-lg shadow p-6 text-gray-600">
                  Schools management for this community — coming soon.
                </div>
              )}
              {activeTab === "team" && (
                <div className="bg-white rounded-lg shadow p-6">
                  <Team />
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectAdminDashboard;
